import { useCallback, useMemo } from 'react';
import { isArrowLeftPressed, isArrowRightPressed } from 'utils';
import useCloseOnEscape from 'hooks/useCloseOnEscape';
import useKeyDown from 'hooks/useKeyDown';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';

type UsePreviewModalNavigationOptions = {
  modal?: ModalRef;
  onPrev?: () => void;
  onNext?: () => void;
};

/**
 * Escape closes the preview modal, arrows move to the sibling file.
 * Shared by the file preview modals (data room node / chat file).
 */
export default function usePreviewModalNavigation({
  modal,
  onPrev,
  onNext,
}: UsePreviewModalNavigationOptions) {
  const isModalOpen = modal?.openState;

  const canNavigate = useMemo(() => !!onPrev || !!onNext, [onNext, onPrev]);

  useCloseOnEscape(modal?.close, {
    capture: true,
    enabled: isModalOpen,
    level: 'overlay',
    element: modal?.container,
  });

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isArrowLeftPressed(e)) onPrev?.();
      if (isArrowRightPressed(e)) onNext?.();
    },
    [onPrev, onNext],
  );

  useKeyDown({ onKeyDown, enabled: isModalOpen && canNavigate });

  return { isModalOpen, canNavigate };
}
